"use client";

/**
 * Positioned dropdown panel for the marketing nav.
 *
 * Disclosure pattern (APG): the trigger button carries `aria-expanded` +
 * `aria-controls`; this panel is plain link content, no menu roles. The shell
 * mounts a panel only while it is open — that gives lazy rendering (nothing in
 * the SSR payload / initial tree) and replays the enter motion on every open.
 * The panel is absolutely positioned under its trigger, so opening causes
 * zero layout shift. Enter motion: 180ms fade + 4px rise (`.mm-panel` in
 * globals.css), disabled under prefers-reduced-motion.
 *
 * `align` keeps wide panels on-screen: leftmost triggers anchor left, middle
 * ones center, rightmost anchor right. Width is clamped to the viewport.
 */

const ALIGN_CLASS = {
  left: "left-0",
  center: "left-1/2 -translate-x-1/2",
  right: "right-0",
} as const;

export function MegaMenu({
  id,
  align = "left",
  width,
  children,
}: {
  /** DOM id referenced by the trigger's aria-controls. */
  id: string;
  align?: keyof typeof ALIGN_CLASS;
  /** Preferred panel width in px; clamped to the viewport. */
  width: number;
  children: React.ReactNode;
}) {
  return (
    <div
      id={id}
      className={`absolute top-full z-50 pt-2.5 ${ALIGN_CLASS[align]}`}
      style={{ width: `min(${width}px, calc(100vw - 2rem))` }}
    >
      <div className="mm-panel rounded-2xl border border-line-subtle bg-bg-raised p-3 shadow-sticky">
        {children}
      </div>
    </div>
  );
}
